/**
 * Streaming Message Component
 * 
 * Renders a message that is being streamed with animated cursor.
 */

import { useEffect, useRef } from 'react';
import { Bot } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

interface StreamingMessageProps {
  /** The text to display (already typewriter-animated from hook) */
  content: string;
  /** Whether the typewriter is still animating */
  isTyping: boolean;
  /** Whether network is still receiving data */
  isStreaming?: boolean;
  /** Callback to skip animation */
  onSkip?: () => void;
}

export function StreamingMessage({ content, isTyping, isStreaming, onSkip }: StreamingMessageProps) {
  const containerRef = useRef<HTMLDivElement>(null);

  // Auto-scroll within the container only (not the page)
  useEffect(() => {
    if (containerRef.current) {
      const scrollParent = containerRef.current.closest('[data-scroll-container]');
      if (scrollParent) {
        scrollParent.scrollTop = scrollParent.scrollHeight;
      }
    }
  }, [content]);

  const showCursor = isTyping || isStreaming;

  return (
    <div className="flex gap-3" ref={containerRef}>
      {/* Avatar */}
      <div className="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center bg-bg-tertiary">
        <Bot className="w-4 h-4 text-text-secondary" />
      </div>

      {/* Message */}
      <div className="flex-1 max-w-[85%]">
        <div className="rounded-lg px-4 py-2.5 bg-bg-tertiary text-text-primary">
          {content ? (
            <div 
              className="text-sm leading-relaxed [&>p]:mb-2 [&>p:last-child]:mb-0 [&>ul]:my-1.5 [&>ul]:ml-4 [&>ul]:list-disc [&>ol]:my-1.5 [&>ol]:ml-4 [&>ol]:list-decimal [&>li]:my-0.5 [&>h1]:text-base [&>h1]:font-semibold [&>h1]:mt-3 [&>h1]:mb-1 [&>h2]:text-sm [&>h2]:font-semibold [&>h2]:mt-2.5 [&>h2]:mb-1 [&>h3]:text-sm [&>h3]:font-medium [&>h3]:mt-2 [&>h3]:mb-0.5 [&>strong]:font-semibold [&>code]:bg-bg-tertiary [&>code]:px-1 [&>code]:py-0.5 [&>code]:rounded [&>code]:text-xs [&>code]:font-mono [&>pre]:bg-bg-tertiary [&>pre]:p-2 [&>pre]:rounded [&>pre]:my-2 [&>pre]:text-xs [&>pre]:overflow-x-auto"
              onClick={isTyping ? onSkip : undefined}
              style={{ cursor: isTyping ? 'pointer' : 'default' }}
              title={isTyping ? 'Click to skip animation' : undefined}
            >
              <ReactMarkdown remarkPlugins={[remarkGfm]}>
                {content}
              </ReactMarkdown>
              {/* Blinking cursor */}
              {showCursor && (
                <span className="inline-block w-1.5 h-4 bg-brand-blue ml-0.5 align-middle animate-blink" />
              )}
            </div>
          ) : (
            <div className="flex items-center gap-2 text-sm text-text-secondary">
              <span className="inline-block w-1.5 h-4 bg-brand-blue animate-blink" />
              <span>Thinking...</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
